import { useState } from "react";
import ImageLightbox from "@/components/comments/image-lightbox";
import { cn } from "@/lib/utils";
import { avatarColor } from "@/utils/call-center.utils";
import { getInitials } from "@/utils/strings";

type Props = {
  name: string;
  /** Absolute URL of the lead's photo; empty/null falls back to initials. */
  avatarUrl?: string | null;
  /** Accent for the initials tile; defaults to the deterministic avatarColor(name). */
  color?: string;
  /** Two-tone gradient background (the conversation-list look) instead of a flat accent. */
  gradient?: boolean;
  /** Size, shape, ring and font-size, e.g. "h-16 w-16 rounded-2xl text-[22px]". */
  className?: string;
  /** When false, the photo is not clickable (tight layouts like toasts). Default true. */
  expandable?: boolean;
};

const BASE_CLASS = "relative grid shrink-0 place-items-center overflow-hidden font-semibold text-white";

/**
 * The lead's avatar everywhere in the app: the uploaded photo when the lead
 * has one, otherwise the usual initials tile. A click on the photo opens it
 * full-size in the shared ImageLightbox (z-250, so it stacks above sheets,
 * modals and call popups) — sales agents identify a lead by the large photo,
 * not the thumbnail. Initials are never clickable.
 */
const LeadAvatar = ({ name, avatarUrl, color, gradient = false, className, expandable = true }: Props) => {
  const [broken, setBroken] = useState(false);
  const [open, setOpen] = useState(false);
  const src = !broken && avatarUrl ? avatarUrl : null;

  if (!src) {
    const accent = color ?? avatarColor(name);
    return (
      <span
        className={cn(BASE_CLASS, className)}
        style={{ background: gradient ? `linear-gradient(135deg, ${accent}, ${accent}99)` : accent }}
      >
        {getInitials(name)}
      </span>
    );
  }

  const photo = (
    <img
      src={src}
      alt={name}
      draggable={false}
      onError={() => setBroken(true)}
      className="h-full w-full object-cover"
    />
  );

  if (!expandable) return <span className={cn(BASE_CLASS, className)}>{photo}</span>;

  return (
    <>
      <button
        type="button"
        aria-label={`View photo of ${name}`}
        title={`View photo of ${name}`}
        // Thumbnails often live inside clickable rows/cards: the zoom must not
        // also trigger the row's navigation.
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(BASE_CLASS, "cursor-zoom-in transition hover:brightness-110", className)}
      >
        {photo}
      </button>
      <ImageLightbox images={[src]} index={open ? 0 : null} onIndexChange={() => undefined} onClose={() => setOpen(false)} />
    </>
  );
};

export default LeadAvatar;
